import classnames from "classnames";
import React, { ComponentType, FC, memo, useCallback } from "react";

import { flexClass } from "../../../Style";
import { IDataTableProps } from "../../types";
import { IRowCellComponentProps, RowCellComponent as RowCellComponentBase } from "../RowCellComponent";
import { ExpandCell as ExpandCellComponentBase, IExpandCellProps } from "./ExpandCell";

export interface IRowComponentProps<T = any>
  extends Pick<IDataTableProps<T>, "columns" | "autoHeight">,
    Pick<IExpandCellProps, "expandIcon" | "collapseIcon"> {
  className?: string;
  expandable: boolean;
  index: number;
  isExpanded: boolean;
  onClick?: (rowData: T) => void;
  onExpandRowClick: (data: T) => void;
  rowData: T;
  TableCellComponent?: ComponentType<IRowCellComponentProps<T>>;
  ExpandCellComponent?: ComponentType<IExpandCellProps>;
  [key: string]: unknown;
}

const RowComponentBase: FC<IRowComponentProps> = ({
  autoHeight,
  className,
  collapseIcon,
  columns,
  expandable,
  expandIcon,
  index,
  isExpanded,
  onClick,
  onExpandRowClick,
  rowData,
  TableCellComponent,
  ExpandCellComponent,
  ...rest
}) => {
  const onRowClickHandler = useCallback(() => {
    if (onClick) {
      onClick(rowData);
    }
  }, [onClick, rowData]);

  const onExpandRowHandler = useCallback(() => {
    onExpandRowClick(rowData);
  }, [onExpandRowClick, rowData]);

  const TableCell = TableCellComponent ? TableCellComponent : RowCellComponentBase;
  const ExpandCell = ExpandCellComponent ? ExpandCellComponent : ExpandCellComponentBase;

  const classNames = classnames(
    flexClass(true),
    "data-table__row",
    {
      "row--height-auto": autoHeight,
      "row--expanded": isExpanded,
    },
    className,
  );

  return (
    <div onClick={onRowClickHandler} className={classNames}>
      {columns.map((column, i) => {
        const { key = "TCell" } = column;
        return <TableCell key={`${key}-${i}`} rowData={rowData} rowIndex={index} column={column} {...rest} />;
      })}
      {expandable ? (
        <ExpandCell
          isExpanded={isExpanded}
          expandRow={onExpandRowHandler}
          expandIcon={expandIcon}
          collapseIcon={collapseIcon}
        />
      ) : null}
    </div>
  );
};

export const RowComponent = memo(RowComponentBase);
